
import Layout from '../components/Layout';
import { Link } from 'react-router-dom';
import { MessageSquare, Mail, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

const Support = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // In a real application, you would send this data to your backend
    // For now, we'll simulate sending an email with a timeout
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Since we can't actually send emails directly from the frontend,
      // we'll show a toast to indicate that it would be sent
      console.log('Support form submitted:', formData);
      
      // In a real implementation, this would be sent to a backend endpoint
      // that would forward the email to lunaxxdev@gmail.com
      
      toast({
        title: "Support request sent",
        description: "We'll get back to you as soon as possible! Your message has been forwarded to our support team. If we dont respond within 7 days, please send us a email.",
      });
      
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      toast({
        title: "Error sending request",
        description: "Please try again or contact us directly via Discord.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="py-24 bg-gradient-to-b from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
        <div className="container-custom">
          <div className="text-center mb-16">
            <div className="inline-block p-3 rounded-full bg-fembot-pink/10 mb-4">
              <MessageSquare className="h-10 w-10 text-fembot-pink" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 dark:text-white">
              Get <span className="text-fembot-pink">Support</span>
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              We're here to help with any questions or issues you might have
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
              <div className="bg-fembot-pink/10 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="h-10 w-10 text-fembot-pink" />
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Check the FAQ</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Find quick answers to common questions in our comprehensive FAQ section.
              </p>
              <Button 
                variant="outline" 
                className="border-fembot-pink text-fembot-pink hover:bg-fembot-pink hover:text-white"
                asChild
              >
                <Link to="/faq">View FAQ</Link>
              </Button>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
              <div className="bg-fembot-pink/10 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-10 w-10 text-fembot-pink" />
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Discord Support</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Join our support Discord server for live assistance and community help.
              </p>
              <Button className="bg-fembot-pink hover:bg-fembot-dark-pink text-white"
                onClick={() => window.open("https://discord.gg/YjtsNfm2Uk", "_blank")}
              >
                Join Support Server
              </Button>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center">
              <div className="bg-fembot-pink/10 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-10 w-10 text-fembot-pink" />
              </div>
              <h3 className="text-xl font-bold mb-2 dark:text-white">Email Support</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Send us an email directly for non-urgent inquiries and business matters.
              </p>
              <Button 
                variant="outline" 
                className="border-fembot-pink text-fembot-pink hover:bg-fembot-pink hover:text-white"
                asChild
              >
                <a href="mailto:Fembot%20Support<lunaxxdev@gmail.com>">Fembot support</a>
              </Button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-center dark:text-white">Contact Us</h2>
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-fembot-pink focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-fembot-pink focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                <select
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-fembot-pink focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  <option value="">Select a topic</option>
                  <option value="general">General Question</option>
                  <option value="billing">Billing Issue</option>
                  <option value="technical">Technical Support</option>
                  <option value="feature">Feature Request</option>
                  <option value="bug">Bug Report</option>
                </select>
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-fembot-pink focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                ></textarea>
              </div>
              <div className="text-center">
                <Button 
                  type="submit" 
                  className="bg-fembot-pink hover:bg-fembot-dark-pink text-white px-8 py-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Support;
